import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Card, CardHeader, Row, Col, Button, Input, Spinner } from "reactstrap";
import TableComponent from "components/shared/Table";
import { getCatalogsByID } from "store/actions/catalogAction";
import usePermissions from "hooks/usePermissions";

function CatalogTable({
  addToggle,
  editToggle,
  deleteToggle,
  setEditData,
  setId,
}) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { catalogs, getLoading, deleteLoading, updateLoading } = useSelector(
    (state) => state.catalog
  );

  const {
    add: addAccess,
    edit: editAccess,
    get: getAccess,
    delete: deleteAccess,
  } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCatalogs, setFilteredCatalogs] = useState([]);

  const allCategoryPresent = useMemo(() => {
    const required = ["breakfast", "lunch", "dinner"];
    return required.every((cat) =>
      catalogs.some((el) => el.category?.toLowerCase().includes(cat))
    );
  }, [catalogs]);

  useEffect(() => {
    dispatch(getCatalogsByID(user?.restaurantID));
  }, [dispatch, user?.restaurantID]);

  useEffect(() => {
    setFilteredCatalogs(catalogs);
  }, [catalogs]);

  const handleAddBtn = () => {
    if (allCategoryPresent) {
      toast.info(
        "Breakfast, Lunch, and Dinner are already present in the catalog. Please remove one to add a new one."
      );
    } else {
      addToggle();
    }
  };

  const returnFormattedTime = (timeStr) => {
    if (!timeStr) return "";
    const date = new Date(`1970-01-01T${timeStr}:00`);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchTerm(value);

      if (!value.trim()) {
        setFilteredCatalogs(catalogs);
        return;
      }

      const lowerSearch = value.toLowerCase();
      setFilteredCatalogs(
        catalogs.filter((el) => el.category.toLowerCase().includes(lowerSearch))
      );
    },
    [catalogs]
  );

  const renderTableRows = () =>
    filteredCatalogs.map((el, idx) => (
      <tr key={idx}>
        <th scope="row">
          {el.category?.charAt(0).toUpperCase() + el.category?.slice(1)}
        </th>
        <td>{returnFormattedTime(el.startTime)}</td>
        <td>{returnFormattedTime(el.endTime)}</td>
        <td>{el.availability}</td>
        <td>
          {el.availabilityDate
            ? `${moment
                .unix(el.availabilityDate.startDate.seconds)
                .format("DD-MMM-YYYY")} to ${moment
                .unix(el.availabilityDate.endDate.seconds)
                .format("DD-MMM-YYYY")}`
            : ""}
        </td>
        {(editAccess || deleteAccess) && (
          <td>
            {editAccess && (
              <Button
                className="btn-sm me-2"
                color="primary"
                onClick={() => {
                  setEditData(el);
                  setId(el.id);
                  editToggle();
                }}
                disabled={updateLoading}
              >
                Edit
              </Button>
            )}
            {deleteAccess && (
              <Button
                className="btn-sm"
                color="danger"
                onClick={() => {
                  setId(el.id);
                  deleteToggle();
                }}
                disabled={deleteLoading}
              >
                Delete
              </Button>
            )}
          </td>
        )}
      </tr>
    ));

  const renderTable = () =>
    getLoading ? (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner color="primary" />
      </div>
    ) : (
      <TableComponent className="align-items-center table-flush" responsive>
        <thead className="thead-light">
          <tr>
            <th scope="col">Categories</th>
            <th scope="col">Start time</th>
            <th scope="col">End time</th>
            <th scope="col">Availability</th>
            <th scope="col">Availability Date</th>
            {(editAccess || deleteAccess) && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {filteredCatalogs.length > 0 ? (
            renderTableRows()
          ) : (
            <tr>
              <td colSpan="6" className="text-center align-middle py-5">
                No Catalog Added
              </td>
            </tr>
          )}
        </tbody>
      </TableComponent>
    );

  return (
    <Row>
      <Col>
        <Card className="shadow">
          <CardHeader className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center px-2 px-lg-4">
            <Col xs="12" lg="3" xl="6" className="mb-3 mb-lg-0">
              <h3 className="mb-0">Catalogs</h3>
            </Col>
            <Row className=" justify-content-end w-100 mx-0">
              <Col xs="12" sm="8" className="mb-3 mb-sm-0">
                <Input
                  placeholder="Search by category..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Col>
              {addAccess && (
                <Col xs="12" sm="4">
                  <Button color="primary" onClick={handleAddBtn} block>
                    Add
                  </Button>
                </Col>
              )}
            </Row>
          </CardHeader>

          {getAccess ? (
            renderTable()
          ) : (
            <Row className="py-4 justify-content-center align-items-center">
              You don't have permission to access this page.
            </Row>
          )}
        </Card>
      </Col>
    </Row>
  );
}

export default CatalogTable;
